import type { PostHogConfig } from "posthog-js";

const DEFAULT_API_HOST = "https://us.i.posthog.com";

function parseReplaySampleRate(raw: string | undefined): number | undefined {
  if (raw == null || raw.trim() === "") return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || n > 1) return undefined;
  return n;
}

/** Non-empty project API key from the wizard / CRA env, or `null` to disable PostHog entirely. */
export function getPostHogPublicToken(): string | null {
  const t = process.env.REACT_APP_PUBLIC_POSTHOG_TOKEN;
  if (t == null || t.trim() === "") return null;
  return t.trim();
}

export function getPostHogInitOptions(): Partial<PostHogConfig> {
  const api_host = (process.env.REACT_APP_PUBLIC_POSTHOG_HOST?.trim() || DEFAULT_API_HOST) as string;
  const replaySampleRate = parseReplaySampleRate(process.env.REACT_APP_PUBLIC_POSTHOG_REPLAY_SAMPLE_RATE);

  const optOutInDev =
    process.env.NODE_ENV === "development" && process.env.REACT_APP_PUBLIC_POSTHOG_IN_DEV !== "true";

  return {
    api_host,
    defaults: "2026-01-30",
    /** Clicks, inputs, navigation — Web analytics baseline. */
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    opt_out_capturing_by_default: optOutInDev,
    session_recording: {
      maskAllInputs: true,
      ...(replaySampleRate != null ? { sampleRate: replaySampleRate } : {}),
    },
    /** Console in session replay (plus `logs` below for the Logs product). */
    enable_recording_console_log: true,
    /** Browser console → PostHog Logs (separate from replay console). */
    logs: {
      captureConsoleLogs: true,
    },
    /**
     * Web Vitals + replay network timing.
     * `web_vitals_attribution` loads a script from PostHog’s assets host; that URL can 302/404
     * (login page) without CORS headers → browser “Cross-Origin Request Blocked” noise. Core
     * vitals still go to PostHog via `src/index.tsx` → `reportWebVitals` → `posthog.capture`.
     */
    capture_performance: {
      web_vitals: true,
      web_vitals_attribution: false,
      network_timing: true,
    },
    /** Frustration / UX signals. */
    capture_dead_clicks: true,
    capture_heatmaps: true,
    /** Uncaught errors, unhandled rejections, `console.error`, React boundary. */
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: true,
    },
    /** Extension-thrown noise is usually off; enable if you need it. */
    error_tracking: {
      captureExtensionExceptions: false,
    },
  };
}
